import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { FC, MutableRefObject, ReactNode } from 'react';

/** One action row of the transcript's ⋯ menu. */
export type TranscriptOverflowItem = {
  key: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  testId?: string;
  /**
   * PLAT-11979 — listed but inert, with this sentence as the hover reason
   * (the design system's disabled-with-reason row). `onClick` is not called.
   */
  disabledNote?: string;
  /** PLAT-11979 — the menu stays open after the click, showing the new rows. */
  keepOpen?: boolean;
  /** Receives the row's rect, for actions that anchor a popover to the row. */
  onClick: (rowRect: DOMRect) => void;
};

/** A row that opens a second-level menu beside it. */
export type TranscriptOverflowSubmenu = {
  kind: 'submenu';
  key: string;
  label: string;
  /** Muted facts after the label — a language's code and state. */
  meta?: string;
  icon?: ReactNode;
  testId?: string;
  items: TranscriptOverflowItem[];
};

export type TranscriptOverflowEntry =
  | TranscriptOverflowItem
  | TranscriptOverflowSubmenu;

export const isOverflowSubmenu = (
  entry: TranscriptOverflowEntry,
): entry is TranscriptOverflowSubmenu =>
  'kind' in entry && entry.kind === 'submenu';

/** A labelled group of rows in the ⋯ menu. */
export type TranscriptOverflowSection = {
  key: string;
  label: string;
  entries: TranscriptOverflowEntry[];
};

type SectionSource = MutableRefObject<TranscriptOverflowSection | null>;

type Registry = {
  register: (key: string, source: SectionSource) => () => void;
  sources: Map<string, SectionSource>;
  /** An open menu listens, so it re-reads a section its registrant rewrote. */
  subscribe: (listener: () => void) => () => void;
  notify: () => void;
  /** Bumps on register / unregister so an open menu re-reads the sources. */
  version: number;
};

const TranscriptOverflowExtrasContext = createContext<Registry | null>(null);

/**
 * PLAT-11979 — lets a component rendered INSIDE the transcript header (through
 * a `headerExtras` slot, so a sibling of the ⋯ menu in the tree) contribute a
 * section to that menu without a Transcript prop. The host wraps `<Transcript>`
 * in this provider; `TranslationToggle` registers its Languages section;
 * `TranscriptOverflowMenu` reads every registered section.
 *
 * Sections are read through refs, not state: the registering component's
 * callbacks are recreated every render, so a setState-per-render registration
 * would re-render the provider's whole subtree in a loop (pitfall 18 class).
 * The ref is rewritten during render for free; the menu reads it when it
 * renders, and while open it re-renders after each registrant commit — only
 * the menu, which is not an ancestor of a registrant, so nothing loops.
 */
export const TranscriptOverflowExtrasProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const sourcesRef = useRef(new Map<string, SectionSource>());
  const listenersRef = useRef(new Set<() => void>());
  const [version, setVersion] = useState(0);
  const register = useCallback((key: string, source: SectionSource) => {
    sourcesRef.current.set(key, source);
    setVersion(v => v + 1);
    return () => {
      if (sourcesRef.current.get(key) === source) {
        sourcesRef.current.delete(key);
        setVersion(v => v + 1);
      }
    };
  }, []);
  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);
  const notify = useCallback(() => {
    listenersRef.current.forEach(listener => listener());
  }, []);
  const value = useMemo<Registry>(
    () => ({
      register,
      sources: sourcesRef.current,
      subscribe,
      notify,
      version,
    }),
    [register, subscribe, notify, version],
  );
  return (
    <TranscriptOverflowExtrasContext.Provider value={value}>
      {children}
    </TranscriptOverflowExtrasContext.Provider>
  );
};

/**
 * Register one section under a stable key. Write the section into the returned
 * ref during render (`ref.current = …`, or `null` to contribute nothing this
 * render). A no-op outside a provider, or while `enabled` is false — one key
 * holds one section, so a second registrant would replace the first.
 */
export const useRegisterTranscriptOverflowSection = (
  key: string,
  enabled = true,
): SectionSource => {
  const registry = useContext(TranscriptOverflowExtrasContext);
  const source = useRef<TranscriptOverflowSection | null>(null);
  const register = registry?.register;
  useEffect(() => {
    if (!register || !enabled) return undefined;
    return register(key, source);
  }, [register, key, enabled]);
  // Every commit may have rewritten the section; tell an open menu.
  const notify = registry?.notify;
  useEffect(() => {
    if (enabled) notify?.();
  });
  return source;
};

/**
 * The sections registered by the header's slots, in registration order. While
 * `live`, the caller re-renders whenever a registrant commits.
 */
export const useTranscriptOverflowExtraSections = (
  live = false,
): TranscriptOverflowSection[] => {
  const registry = useContext(TranscriptOverflowExtrasContext);
  const [, rerender] = useState(0);
  const subscribe = registry?.subscribe;
  useEffect(() => {
    if (!subscribe || !live) return undefined;
    return subscribe(() => rerender(n => n + 1));
  }, [subscribe, live]);
  if (!registry) return EMPTY;
  const sections: TranscriptOverflowSection[] = [];
  registry.sources.forEach(source => {
    if (source.current) sections.push(source.current);
  });
  return sections;
};

const EMPTY: TranscriptOverflowSection[] = [];
